import {Injectable} from "@angular/core";
import {
  ParameterRule,
  QueryNode,
} from "@ispm-client/modules/query-builder/models/query-builder-config";
import {BiomarkerService} from "./biomarker-service";
import {DisorderService} from "./disorder-service";
import {FactorBaseService} from "./factor-base-service";
import {LabTestService} from "./labtest-service";
import {RiskService} from "./risk-service";
import {RelapseService} from "./relapse-service";
import {TherapyService} from "./therapy-service";
import {PatientFactorService} from "./patient-factor.service";

@Injectable()
export class QueryService {
  static readonly NOT = "NOT ";
  static readonly SPACE = FactorBaseService.SPACE;
  static readonly EMPTY_STRING = "";
  static readonly LEFT_PAREN = "(";
  static readonly RIGHT_PAREN = ")";
  static readonly BIOMARKER = "biomarker";
  static readonly DISEASE = "disease";
  static readonly PATIENT = "patient";
  static readonly THERAPY = "therapy";
  static readonly RISK = "risk";
  static readonly LABTEST = "labtest";
  static readonly RELAPSE = "relapse";

  constructor(private biomarkerService: BiomarkerService, private disorderService: DisorderService, private labtestService: LabTestService,
              private riskService: RiskService, private relapseService: RelapseService, private patientService: PatientFactorService, private therapyService: TherapyService) {
  }

  public getRepresentation(queryNode: QueryNode): string[] {
    let results: string[];
    results = this.toString(queryNode);
    return results;
  }

  private toString(queryNode: QueryNode): string[] {
    var results: string[] = new Array();
    let condition = queryNode.condition.toUpperCase();  //and or or
    let rules = queryNode.rules;
    let type = queryNode.type.toUpperCase();  //HEAD, PARAMETER, PARAMETER_SET

    let index:number = 0;
    let setIndex:number = 0;

    switch (type) {
      case "HEAD": {
        for (let rule of rules) {
          let ruleCondition = rule.condition.toUpperCase();  //and or or
          let parameterRules = rule.rules;
          let ruleType = rule.type.toUpperCase();  //PARAMETER, PARAMETER_SET
          switch (ruleType) {
            case "PARAMETER": {
              let negate = rule.negate;
              let prefix:string = QueryService.EMPTY_STRING;
              let parameterString = QueryService.EMPTY_STRING;
              let parameter = rule.parameter;
              if (parameter === undefined)
              //user entered an empty parameter
                break;
              parameterString = this.getDisplayName(rule);
              parameterString = this.negateParameter(negate, parameterString);
              prefix = this.setParameterPrefix(index, condition, prefix);
              parameterString = [prefix, parameterString].filter(x => x).join(QueryService.SPACE);
              results.push(parameterString);
              index++;
              break;
            }

            case "PARAMETER_SET": {
              let newParameterSet:boolean = true;
              let prefix:string = QueryService.EMPTY_STRING;
              let conditionBetweenSets:string = condition;
              if (parameterRules.length > 0) {
                for (let parameterRule of parameterRules) {
                  let negate = parameterRule.negate;
                  let parameterString = QueryService.EMPTY_STRING;
                  let parameter = parameterRule.parameter;
                  if (parameter === undefined)
                  //user entered an empty parameter
                    break;
                  parameterString = [prefix, this.getDisplayName(parameterRule)].filter(x => x).join(QueryService.SPACE);
                  parameterString = this.negateParameter(negate, parameterString);
                  prefix = this.setParameterSetPrefix(setIndex, newParameterSet, conditionBetweenSets, ruleCondition, prefix);
                  if (newParameterSet) {
                    parameterString = QueryService.LEFT_PAREN + parameterString;
                    newParameterSet = false;
                  }
                  parameterString = [prefix, parameterString].filter(x => x).join(QueryService.SPACE);
                  results.push(parameterString);
                  prefix = QueryService.EMPTY_STRING;
                  setIndex++;
                  newParameterSet = false;
                } //end of for loop
                //we're here at end of a parameter set
              }

              newParameterSet = true;
              //we just finished a parameter set so add closing parenthesis to last result
              let length: number = results.length;
              results[length-1] = results[length-1] + QueryService.RIGHT_PAREN;
              break;
            }

            default: {
              break;
            }
          }
        }
        break;
      }
      default: {
        break;
      }
    }

    //if we have a single paramater parameter set then remove parentheses
    this.adjustParentheses(results);
    return results;
  }

  private getDisplayName(parameterRule: ParameterRule) : string {
    let parameter: String = parameterRule.parameter.toLowerCase();
    if (parameter.startsWith(QueryService.BIOMARKER)) {
      return this.biomarkerService.getDisplayName(parameterRule);
    }
    else if (parameter.startsWith(QueryService.DISEASE)) {
      return this.disorderService.getDisplayName(parameterRule);
    }
    else if (parameter.startsWith(QueryService.PATIENT)) {
      return this.patientService.getDisplayName(parameterRule);
    }
    else if (parameter.startsWith(QueryService.THERAPY)) {
      return this.therapyService.getDisplayName(parameterRule);
    }
    else if (parameter.startsWith(QueryService.RISK)) {
      return this.riskService.getDisplayName(parameterRule);
    }
    else if (parameter.startsWith(QueryService.RELAPSE)) {
      return this.relapseService.getDisplayName(parameterRule);
    }
    else if (parameter.startsWith(QueryService.LABTEST)) {
      return this.labtestService.getDisplayName(parameterRule);
    }
  }

  private adjustParentheses(results: string[]) {
    let length: number = results.length;
    for(let i = 0; i<length; i++) {
      let parameterSet = results[i];
      if (parameterSet.includes(QueryService.LEFT_PAREN) && parameterSet.includes(QueryService.RIGHT_PAREN)) {
        //we have a left paren and a right paren on one parameter, so remove both parens
        let temp: string = parameterSet.replace(QueryService.LEFT_PAREN, QueryService.EMPTY_STRING);
        results[i] = temp.replace(QueryService.RIGHT_PAREN, QueryService.EMPTY_STRING);
      }
    }
  }

  private setParameterPrefix(index: number, condition: string, prefix: string): string {
    if (index > 0)
      prefix = condition;
    else
      prefix = QueryService.EMPTY_STRING;
    return prefix;
  }

  private setParameterSetPrefix(setIndex: number, newParameterSet: boolean, conditionBetweenSets: string, ruleCondition: string,  prefix: string) : string {
    if (setIndex > 0)
      if (newParameterSet) {
        prefix = conditionBetweenSets;
      }
      else {
        prefix = ruleCondition.toUpperCase();
      }
    else
      prefix = QueryService.EMPTY_STRING;
    return prefix;
  }

  private negateParameter(ruleNegate: boolean, parameterString: string): string {
    const NOT = QueryService.NOT + QueryService.SPACE;
    let negate = (ruleNegate === undefined ? false : ruleNegate);
    if (negate)
      parameterString = NOT + parameterString;
    return parameterString;
  }

  private emptyRules(parameterRules: any[]): boolean {
    let length = parameterRules.length;
    for (let i = 0; i < length; i++) {
      if (parameterRules[i].parameter !== undefined)
        return false;
      if (parameterRules[i].rules.length > 0)
        return this.emptyRules(parameterRules[i].rules );
    }
    return true;
  }

  // public static getDefaultQueryNode(): QueryNode {
  //   let defaultQueryNode: QueryNode = {
  //     negate: false,
  //     condition: Condition.AND,
  //     type: NodeType.HEAD,
  //     rules: []
  //   };
  //   return defaultQueryNode;
  // }

}
